import Link from 'next/link';
import React, { FC, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IoChevronDown, IoDuplicateOutline } from 'react-icons/io5';
import { TbArchive, TbArchiveOff, TbPencil } from 'react-icons/tb';

import { Button, ButtonProps, Group, GroupProps, Menu, Text } from '@mantine/core';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import { ComponentHelpers } from 'helpers/ComponentHelpers';

import { ComponentBulkEditSave } from 'components/component-datasheet/components/ComponentBulkEditSave';
import { useDatasheetProductSeriesComponents } from 'components/component-datasheet/hooks/use-datasheet-product-series-components';
import { PublishButton } from 'components/component-signup/components/PublishButton';
import { DatasheetMode, useDatasheetModeState } from 'components/datasheet/DatasheetMode';
import { useSubmitForm } from 'components/forms/Form';
import { ReferenceDesignButton } from 'components/reference-design-button/ReferenceDesignButton';
import { ComponentService } from 'services/ComponentService';
import { LocalNotificationService } from 'services/LocalNotificationService';

import { useComponentContext } from '../hooks/use-component-context';

const ComponentDatasheetActionsEdit: FC<{ buttonSize?: ButtonProps['size']; wrapperProps?: GroupProps }> = ({
    buttonSize = 'sm',
    wrapperProps,
}) => {
    const { component } = useComponentContext();
    const [archivedAt, id, manufacturer, productSeries, visibility] = useWatch({
        name: ['archivedAt', 'id', 'manufacturer', 'productSeries', 'visibility'],
    });

    const { reset, setValue } = useFormContext();
    const values = useWatch();
    const { submit: submitForm } = useSubmitForm();
    const [mode, setMode] = useDatasheetModeState();

    const [isSaving, setSaving] = useState(false);

    const { components: variants } = useDatasheetProductSeriesComponents();

    const isPublishable = visibility === 'private';

    const saveComponent = async () => {
        setSaving(true);

        try {
            await submitForm();

            postSaveAction();

            LocalNotificationService.showSuccess({
                message: 'Product saved successfully!',
            });
        } catch (error) {
            LocalNotificationService.showError({
                message: error instanceof Error ? error.message : 'Something went wrong, try again.',
            });
        } finally {
            // duplicate will redirect, so keep showing the spinner
            if (mode !== DatasheetMode.DUPLICATE) {
                setSaving(false);
            }
        }
    };

    const postSaveAction = () => {
        if (mode === DatasheetMode.EDIT) {
            setMode(DatasheetMode.VIEW);
        }
    };

    const editComponent = () => {
        setMode(DatasheetMode.EDIT);
    };

    const cancelEdit = () => {
        reset();
        setMode(DatasheetMode.VIEW);
    };

    const duplicateComponent = () => {
        ComponentService.navigate.duplicate(id);
    };

    const editProductSeries = () => {
        ComponentService.navigate.bulk(CollectionHelpers.id(manufacturer)!, productSeries);
    };

    const attemptValueUpdate = (key: string, newValue: any, successMessage = 'Component updated!') => {
        const oldValue = values[key];

        setValue(key, newValue);
        submitForm()
            .then(() => {
                LocalNotificationService.showSuccess({
                    message: successMessage,
                });
            })
            .catch(() => {
                setValue(key, oldValue);
            });
    };

    const archiveComponent = () => {
        attemptValueUpdate('archivedAt', new Date().toISOString(), 'Component archived successfully!');
    };

    const unarchiveComponent = () => {
        attemptValueUpdate('archivedAt', null, 'Component unarchived successfully!');
    };

    const makeComponentPublic = () => {
        attemptValueUpdate('visibility', 'public', 'Component is now public!');
    };

    const hasMissingRequiredFields = !values.name;

    if (mode === DatasheetMode.CREATE || mode === DatasheetMode.DUPLICATE) {
        return (
            <Group {...wrapperProps}>
                <Button
                    size={buttonSize}
                    variant="filled"
                    disabled={hasMissingRequiredFields}
                    onClick={saveComponent}
                    loading={isSaving}
                >
                    Save new product
                </Button>
            </Group>
        );
    }

    if (mode === DatasheetMode.EDIT) {
        return (
            <Group {...wrapperProps}>
                <Button size={buttonSize} variant="subtle" onClick={cancelEdit}>
                    Discard changes
                </Button>
                <Button size={buttonSize} variant="filled" onClick={saveComponent} loading={isSaving}>
                    Save changes
                </Button>
            </Group>
        );
    }

    if (mode === DatasheetMode.BULK) {
        return (
            <Group {...wrapperProps}>
                <ComponentBulkEditSave size={buttonSize} variant="filled" />
            </Group>
        );
    }

    return (
        <Group {...wrapperProps}>
            <Menu trigger="click-hover" position="bottom-end">
                <Menu.Target>
                    <Button
                        size={buttonSize}
                        variant="subtle"
                        onClick={editComponent}
                        leftSection={<TbPencil size={12} />}
                        rightSection={<IoChevronDown size={12} />}
                    >
                        Edit
                    </Button>
                </Menu.Target>

                <Menu.Dropdown>
                    <Menu.Item onClick={editComponent} leftSection={<TbPencil size={12} />}>
                        Edit Product
                    </Menu.Item>

                    {variants && variants.length > 1 && (
                        <Menu.Item onClick={editProductSeries} leftSection={<TbPencil size={12} />}>
                            Edit Product Series
                        </Menu.Item>
                    )}

                    {component.type !== 'other' && (
                        <Menu.Item
                            component={Link}
                            href={`${ComponentHelpers.urls.generate(component.id)}`}
                            leftSection={
                                <Text mt={2} fw={900} fz={10} c="brand.4">
                                    AI
                                </Text>
                            }
                        >
                            <span className="gradient-text-default">Generate Datasheet</span>
                        </Menu.Item>
                    )}

                    {archivedAt ? (
                        <Menu.Item onClick={unarchiveComponent} leftSection={<TbArchiveOff size={12} />}>
                            Unarchive
                        </Menu.Item>
                    ) : (
                        <Menu.Item color="red" onClick={archiveComponent} leftSection={<TbArchive size={12} />}>
                            Archive
                        </Menu.Item>
                    )}
                </Menu.Dropdown>

                <Button
                    size={buttonSize}
                    variant="subtle"
                    onClick={duplicateComponent}
                    leftSection={<IoDuplicateOutline size={12} />}
                >
                    Add variant
                </Button>
            </Menu>

            <ReferenceDesignButton
                size={buttonSize}
                component={component}
                variant={isPublishable ? 'subtle' : 'filled'}
            />
            {isPublishable && <PublishButton size={buttonSize} handlePublish={makeComponentPublic} variant="filled" />}
        </Group>
    );
};

export { ComponentDatasheetActionsEdit };
